import { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../../components/ui/sheet';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';

const inr = n => (n == null ? '—' : `₹${Math.round(n).toLocaleString('en-IN')}`);
const Line = ({ k, v, b }) => <div className="flex justify-between text-xs py-1 border-b border-slate-50"><span className="text-slate-500">{k}</span><span className={b ? 'font-bold' : ''}>{v}</span></div>;

const Actions = ({ r, deadlinePassed, onDone }) => {
  const { user } = useAuth();
  const admin = user?.role === 'admin';
  const [reason, setReason] = useState('');
  const go = async to => {
    try { await api.post(`/reports/fp-incentive/${r.emp_code}/${r.month}/transition`, { to, reason }); toast.success(`Moved to ${to}`); setReason(''); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid="fp-actions">
      {r.status === 'provisional' && <Button size="sm" onClick={() => go('finalized')} disabled={!deadlinePassed} title={deadlinePassed ? '' : `Payments can arrive until ${r.deadline}`} data-testid="fp-finalize-btn">Finalize</Button>}
      {r.status === 'finalized' && admin && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => go('paid')} data-testid="fp-paid-btn">Mark paid</Button>}
      {r.status !== 'provisional' && admin && <>
        <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason to reopen" className="h-8 w-44 text-xs" data-testid="fp-reopen-reason" />
        <Button size="sm" variant="outline" disabled={reason.trim().length < 5} onClick={() => go(r.status === 'paid' ? 'finalized' : 'provisional')} data-testid="fp-reopen-btn">Reopen</Button>
      </>}
      {r.status === 'provisional' && !deadlinePassed && <span className="text-[11px] text-amber-700">Finalize after {r.deadline}</span>}
    </div>
  );
};

export const FpRowDrawer = ({ emp, month, deadlinePassed, onClose, onChanged }) => {
  const [r, setR] = useState(null);
  const load = () => api.get(`/reports/fp-incentive/${emp}/${month}`).then(x => setR(x.data)).catch(e => toast.error(e.response?.data?.detail || 'Failed'));
  useEffect(() => { setR(null); if (emp) load(); }, [emp, month]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Sheet open={!!emp} onOpenChange={v => !v && onClose()}>
      <SheetContent className="w-[760px] sm:max-w-[760px] overflow-y-auto" data-testid="fp-drawer">
        {!r ? <Loader2 className="w-5 h-5 animate-spin mt-10 text-slate-400" /> : <>
          <SheetHeader><SheetTitle>{r.emp_name} · {r.month}</SheetTitle></SheetHeader>
          <div className="grid grid-cols-2 gap-6 mt-3">
            <div>
              <Line k="Expected target" v={inr(r.expected_target)} />
              <Line k={`Invoiced (${r.invoice_count} invoices, incl. tax)`} v={inr(r.invoiced)} />
              <Line k="Left out — payment pending" v={`− ${inr(r.left_out_pending)}`} />
              <Line k="Left out — credit note" v={`− ${inr(r.left_out_cn)}`} />
              {r.left_out_other > 0 && <Line k="Left out — write-off" v={`− ${inr(r.left_out_other)}`} />}
              <Line k={`Fully Paid Sales (${r.qualifying_count} invoices)`} v={inr(r.fully_paid_sales)} b />
            </div>
            <div>
              <Line k="Achievement" v={`${r.achievement_pct}% ${r.gate_pass ? '✓' : '✗'}`} b />
              <Line k={`Slab · ${r.bracket_label}`} v={inr(r.bracket_incentive)} />
              <Line k="Slab paid" v={inr(r.incentive_portion)} /><Line k="Zoho commission paid" v={`${inr(r.commission_portion)} of ${inr(r.zoho_commission)}`} />
              <Line k="Payable" v={inr(r.final_incentive)} b />
              <p className="text-[11px] text-slate-500 mt-1">{r.rule_note}</p>
            </div>
          </div>
          <div className="mt-4"><Actions r={r} deadlinePassed={deadlinePassed} onDone={() => { load(); onChanged(); }} /></div>
          <h4 className="text-xs font-bold text-slate-800 mt-5 mb-2">Invoices — paid by {r.deadline}</h4>
          <table className="w-full text-xs" data-testid="fp-invoices">
            <thead className="text-[10px] uppercase tracking-wider text-slate-400"><tr><th className="text-left py-1">Invoice</th><th className="text-left">Date</th><th className="text-left">Customer</th>
              <th className="text-right">Total</th><th className="text-right">Paid by deadline</th><th className="text-left pl-3">Counted?</th></tr></thead>
            <tbody>{r.invoices.map(i => (
              <tr key={i.invoice_id} className={`border-t border-slate-100 ${i.qualifies ? '' : 'bg-rose-50/40'}`} data-testid={`fp-inv-${i.invoice_number}`}>
                <td className="py-1.5"><a href={i.url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline inline-flex items-center gap-1">{i.invoice_number}<ExternalLink className="w-3 h-3" /></a></td>
                <td className="whitespace-nowrap">{i.date}</td><td>{i.customer_name}</td><td className="text-right">{inr(i.total)}</td><td className="text-right">{inr(i.paid_by_deadline)}</td>
                <td className="pl-3">{i.qualifies ? <span className="text-emerald-700 font-semibold">Counted</span> : <span className="text-rose-700">{i.reasons.join('; ')}{i.credit_notes?.length ? ` (${i.credit_notes.map(c => c.creditnote_number).join(', ')})` : ''}</span>}</td>
              </tr>))}</tbody>
          </table>
          {r.timeline?.length > 0 && <><h4 className="text-xs font-bold text-slate-800 mt-5 mb-1">Status history</h4>
            {r.timeline.map((t, i) => <p key={i} className="text-[11px] text-slate-500">{new Date(t.at).toLocaleString('en-IN')} · {t.by}: {t.from} → {t.to}{t.reason ? ` (“${t.reason}”)` : ''}</p>)}</>}
        </>}
      </SheetContent>
    </Sheet>
  );
};
